import React, { useState, useRef, useEffect, useCallback } from "react";
import DateField from "../../common/datetime/DateField";
import "./css/ScamActivityMap.css";

const RADIUS_OPTIONS = [5, 10, 25, 50];

const DATE_PRESETS = [
  { label: "Last 30 days", months: 1 },
  { label: "Last 3 months", months: 3 },
  { label: "Last 6 months", months: 6 },
  { label: "Last year", months: 12 },
  { label: "Custom range…", months: null },
];

const SCAM_TYPE_OPTIONS = [
  { value: "ROMANCE_SCAM", label: "Romance Scam" },
  { value: "PHISHING", label: "Phishing / Smishing" },
  { value: "TECH_SUPPORT_SCAM", label: "Tech Support Scam" },
  { value: "INVESTMENT_SCAM", label: "Investment Scam" },
  { value: "EMPLOYMENT_SCAM", label: "Employment Scam" },
  { value: "ONLINE_SHOPPING_SCAM", label: "Online Shopping Scam" },
  { value: "GOVERNMENT_IMPERSONATION", label: "Government Impersonation" },
  { value: "PHONE_SCAM", label: "Phone Scam" },
  { value: "LOTTERY_SCAM", label: "Lottery Scam" },
  { value: "CHARITY_SCAM", label: "Charity Scam" },
  { value: "OTHER", label: "Other" },
];

const US_STATES = [
  { value: "", label: "State" },
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
];

const COUNTRIES = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
  { value: "MX", label: "Mexico" },
];

function isoDate(d) {
  return d.toISOString().split("T")[0];
}

function monthsAgo(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return isoDate(d);
}

/**
 * Address input + search parameter controls for the Scam Activity Map.
 *
 * @param {string}   savedAddress      - Pre-populated from user profile
 * @param {boolean}  loading           - Whether a search is in progress
 * @param {Function} onSearch          - Called with the search params object
 * @param {Function} onSaveAddress     - Called with the address string when checkbox is checked
 */
function ScamActivityMapSearch({
  savedAddress,
  loading,
  onSearch,
  onSaveAddress,
}) {
  const [city, setCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [country, setCountry] = useState("US");
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [datePreset, setDatePreset] = useState(2); // index into DATE_PRESETS (default: 6 months)
  const [customFrom, setCustomFrom] = useState(monthsAgo(6));
  const [customTo, setCustomTo] = useState(isoDate(new Date()));
  const [selectedTypes, setSelectedTypes] = useState([]); // empty = all
  const [saveAddress, setSaveAddress] = useState(false);
  const [typesExpanded, setTypesExpanded] = useState(false);
  const [otherCustomValue, setOtherCustomValue] = useState("");
  const cityRef = useRef(null);

  // ── Google Places Autocomplete ─────────────────────────────────────────────
  useEffect(() => {
    if (!window.google || !window.google.maps || !cityRef.current) return;
    const autocomplete = new window.google.maps.places.Autocomplete(
      cityRef.current,
      { types: ["(cities)"], componentRestrictions: { country: "us" } },
    );
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place.address_components) {
        const comps = place.address_components;
        const get = (type) => comps.find((c) => c.types.includes(type));
        setCity(
          get("locality")?.long_name ||
            get("sublocality_level_1")?.long_name ||
            "",
        );
        setAddrState(get("administrative_area_level_1")?.short_name || "");
        setCountry(get("country")?.short_name || "US");
      }
    });
  }, []);

  // ── Computed date range ────────────────────────────────────────────────────
  const getDateRange = useCallback(() => {
    const preset = DATE_PRESETS[datePreset];
    if (preset.months === null) {
      return { dateFrom: customFrom, dateTo: customTo };
    }
    return { dateFrom: monthsAgo(preset.months), dateTo: isoDate(new Date()) };
  }, [datePreset, customFrom, customTo]);

  // ── Build full address string from structured fields ───────────────────────
  const buildFullAddress = useCallback(() => {
    return [city, addrState, country !== "US" ? country : ""]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
  }, [city, addrState, country]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSearch = () => {
    if (!city.trim() || !addrState) return;
    const fullAddress = buildFullAddress();
    const { dateFrom, dateTo } = getDateRange();

    if (saveAddress && onSaveAddress) {
      onSaveAddress(fullAddress);
    }

    onSearch({
      address: fullAddress,
      radiusMiles,
      dateFrom,
      dateTo,
      scamTypes: selectedTypes,
      otherTypeSearch: selectedTypes.includes("OTHER")
        ? otherCustomValue.trim()
        : "",
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const toggleType = (value) => {
    setSelectedTypes((prev) => {
      const isSelected = prev.includes(value);
      if (value === "OTHER" && isSelected) setOtherCustomValue("");
      return isSelected ? prev.filter((v) => v !== value) : [...prev, value];
    });
  };

  return (
    <div className="sam-search">
      {/* ── Address ── */}
      <div className="sam-search__field">
        <label className="sam-search__label">Search near this location</label>

        {/* City / State */}
        <div className="sam-search__addr-row">
          <input
            id="sam-city"
            ref={cityRef}
            type="text"
            className="sam-search__input sam-search__input--city"
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="address-level2"
          />
          <select
            id="sam-state"
            className="sam-search__select sam-search__select--state"
            value={addrState}
            onChange={(e) => setAddrState(e.target.value)}
            aria-label="State"
          >
            {US_STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Country */}
        <select
          id="sam-country"
          className="sam-search__select"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          aria-label="Country"
        >
          {COUNTRIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {/* ── Radius ── */}
      <div className="sam-search__field">
        <label className="sam-search__label">Search radius</label>
        <div
          className="sam-search__radius-group"
          role="group"
          aria-label="Search radius"
        >
          {RADIUS_OPTIONS.map((r) => (
            <button
              key={r}
              type="button"
              className={`sam-search__radius-btn${radiusMiles === r ? " sam-search__radius-btn--active" : ""}`}
              onClick={() => setRadiusMiles(r)}
              aria-pressed={radiusMiles === r}
            >
              {r} mi
            </button>
          ))}
        </div>
      </div>

      {/* ── Date range ── */}
      <div className="sam-search__field">
        <label className="sam-search__label" htmlFor="sam-date-preset">
          Time period
        </label>
        <select
          id="sam-date-preset"
          className="sam-search__select"
          value={datePreset}
          onChange={(e) => setDatePreset(Number(e.target.value))}
        >
          {DATE_PRESETS.map((p, i) => (
            <option key={i} value={i}>
              {p.label}
            </option>
          ))}
        </select>

        {DATE_PRESETS[datePreset].months === null && (
          <div className="sam-search__custom-dates">
            <DateField
              id="sam-date-from"
              label="From"
              value={customFrom}
              max={customTo || isoDate(new Date())}
              onChange={setCustomFrom}
            />
            <DateField
              id="sam-date-to"
              label="To"
              value={customTo}
              min={customFrom}
              max={isoDate(new Date())}
              onChange={setCustomTo}
            />
          </div>
        )}
      </div>

      {/* ── Scam type filter ── */}
      <div className="sam-search__field">
        <button
          type="button"
          className="sam-search__types-toggle"
          onClick={() => setTypesExpanded((p) => !p)}
          aria-expanded={typesExpanded}
        >
          Filter by scam type
          {selectedTypes.length > 0 && (
            <span className="sam-search__types-badge">
              {selectedTypes.length} selected
            </span>
          )}
          <span className="sam-search__chevron">
            {typesExpanded ? "▲" : "▼"}
          </span>
        </button>

        {typesExpanded && (
          <div className="sam-search__types-list">
            <label className="sam-search__type-label sam-search__type-label--all">
              <input
                type="checkbox"
                checked={selectedTypes.length === 0}
                onChange={() => {
                  setSelectedTypes([]);
                  setOtherCustomValue("");
                }}
              />
              <span>All</span>
            </label>
            {SCAM_TYPE_OPTIONS.map((opt) =>
              opt.value === "OTHER" ? (
                <div key={opt.value} className="sam-search__type-item">
                  <label className="sam-search__type-label">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(opt.value)}
                      onChange={() => toggleType(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                  {selectedTypes.includes("OTHER") && (
                    <input
                      type="text"
                      className="sam-search__other-input"
                      placeholder="Specify type…"
                      value={otherCustomValue}
                      onChange={(e) => setOtherCustomValue(e.target.value)}
                    />
                  )}
                </div>
              ) : (
                <label key={opt.value} className="sam-search__type-label">
                  <input
                    type="checkbox"
                    checked={selectedTypes.includes(opt.value)}
                    onChange={() => toggleType(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ),
            )}
            {selectedTypes.length > 0 && (
              <button
                type="button"
                className="sam-search__clear-types"
                onClick={() => {
                  setSelectedTypes([]);
                  setOtherCustomValue("");
                }}
              >
                Clear filters (show all types)
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Save address checkbox ── */}
      <label className="sam-search__save-label">
        <input
          type="checkbox"
          checked={saveAddress}
          onChange={(e) => setSaveAddress(e.target.checked)}
        />
        <span>
          Save this address to my profile for next time
          {savedAddress && (
            <span className="sam-search__save-note">
              {" "}
              (replaces your currently saved address)
            </span>
          )}
        </span>
      </label>

      {/* ── Search button ── */}
      <button
        type="button"
        className="sam-search__submit"
        onClick={handleSearch}
        disabled={loading || !city.trim() || !addrState}
      >
        {loading ? "Searching…" : "🔍 Search for Nearby Scams"}
      </button>
    </div>
  );
}

export default ScamActivityMapSearch;
